// calls all apis for ya

import Axios from 'axios';
import moment from 'moment';
import { get, isEmpty } from 'lodash';

import rootStore from '../store/store';
import { apiBaseUrl, constants } from '../constants/configuration';
import {
  defferedPingServerInSeconds,
  enableProactiveTokenRefresh,
  internetDisconnectivityHandledAPIEndpointBlacklist,
  internetErrorResponseStoreDispatchDelay,
  serverErrorCodes,
  storageTypes,
  tokenExcludedAPIEndpointBlacklist,
} from '../constants/globals';
import { apiErrorResponseAction } from '../actions/app.action';
import {
  checkInternetConnectivity,
  getAppInStartupPhase,
  isJWTTokenExpired,
  isNativeWebView,
  isAuth401Error,
} from './helpers';

import { getItem, setItem } from './storage';
import { differenceBetween } from './qwikyLocalizationHelper';
import { getIsApiRequestOnHold, setIsApiRequestOnHold } from './helpers';
import resourcesConfig from '../resources';
import { ATTEMPT_LOGIN_SUCCESS, DISPATCH_AUTH_TOKEN } from '../actions/authentication/actionType';
import {
  notifyAuthTokenUpdated,
  registerWorkerTokenRefreshHandler,
} from './authTokenSync';

const { store, persistor } = rootStore;

const TYPE_JSON = 'application/json';

let pendingRequests = [];
let refreshPromise = null;
let lastRefreshAt = 0;
let sessionExpired = false;

export const buildAPIInstance = ({ contentType = TYPE_JSON }) => {
  const contextualTimeStamp = moment().format();
  const nativeAppConfig = window.nativeAppConfig;
  // Get app configuration from injected native app config or use defaults
  const appName = get(nativeAppConfig, 'appName');
  const appId = get(nativeAppConfig, 'appId');

  const headers = {
    common: {
      Accept: TYPE_JSON,
      'Content-Type': contentType,
    },
    post: {
      'Content-Type': TYPE_JSON,
    },
  };

  if (!isEmpty(appName)) {
    headers.common['x-app-name'] = appName;
  }
  if (!isEmpty(appId)) {
    headers.common['x-app-id'] = appId;
  }

  const objAxios = Axios.create({
    baseURL: apiBaseUrl,
    headers,
  });

  objAxios.interceptors.request.use(async (config) => {
    if (!config.url) {
      return config;
    }

    if (
      getIsApiRequestOnHold() &&
      !tokenExcludedAPIEndpointBlacklist.some((endpoint) => config.url.includes(endpoint))
    ) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject, config, phase: 'request' });
      });
    }
    const authData = getItem(storageTypes.localStorage, 'AuthData');
    const accessToken = get(authData, 'accessToken', '');
    if (
      !isEmpty(accessToken) &&
      !tokenExcludedAPIEndpointBlacklist.some((endpoint) => config.url.includes(endpoint))
    ) {
      const isTokenExpired = isJWTTokenExpired(accessToken);
      const isAppInStartupPhase = getAppInStartupPhase();
      if (isTokenExpired && isAppInStartupPhase && enableProactiveTokenRefresh) {
        const error = new Error('Request failed');
        error.response = {
          data: {
            isError: true,
            code: serverErrorCodes.invalidToken,
            message: 'Authentication error',
            detail: 'Authentication error',
            status: 'UNAUTHORIZED',
            statusCode: 401,
          },
          status: 401,
        };
        error.config = config;
        const newToken = await handleAuthenticationError(error, { retryOriginal: false });
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${newToken}`;
        return config;
      } else {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    const isExternalLoyaltyEnabled = get(
      store.getState(),
      'common.resources.isExternalLoyaltyEnabled',
      resourcesConfig.isExternalLoyaltyEnabled,
    );
    if (isExternalLoyaltyEnabled) {
      config.headers['x-ext-loyalty'] = 'xeno';
    }

    // const geoLocation = localStorage.getItem('geoLocation');
    // if (geoLocation) {
    //   config.headers.common['location'] = geoLocation; // set updated geoLocation in req headers
    // }
    // exempting internet connectivity logic for native as the same is already handled at the native level
    if (!isNativeWebView) {
      const requestURLPart = config.url;
      const isRequestURLBlacklisted = internetDisconnectivityHandledAPIEndpointBlacklist.some(
        (endpoint) => requestURLPart.indexOf(endpoint) > -1,
      );
      if (!isRequestURLBlacklisted) {
        const currentTimeStamp = getItem(storageTypes.sessionStorage, 'currentTimeStamp');
        const differenceInTimeStamps = differenceBetween(
          contextualTimeStamp,
          currentTimeStamp,
          'seconds',
        );
        if (differenceInTimeStamps > defferedPingServerInSeconds) {
          // update currentTimeStamp with latest time stamp
          setItem(storageTypes.sessionStorage, 'currentTimeStamp', moment().format());
          checkInternetConnectivity().then((isOnline) => {
            if (!isOnline) {
              // Generate a custom error response containing a user-friendly message to indicate internet disconnectivity.
              const errorResponse = {
                message: constants.globalErrorInfo.internetDisconnectivityErrorMessage,
              };
              console.error('Internet error response:', errorResponse);
              // Jump over all updates to errorResponse slice in redux store, if any, and then dispatch this one.
              setTimeout(() => {
                store.dispatch(apiErrorResponseAction(errorResponse));
              }, internetErrorResponseStoreDispatchDelay);
            }
          });
        }
      }
    }

    return config;
  });

  const handleTokenRefresh = async (authData) => {
    const refreshToken = get(authData, 'refreshToken', '');
    if (!refreshToken || isJWTTokenExpired(refreshToken)) {
      const error = new Error('Refresh token expired');
      error.response = { status: 401, data: { code: 'REFRESH_TOKEN_EXPIRED' } };
      throw error;
    }

    const response = await Axios.post(
      `${apiBaseUrl}/api/v1/auth/token/refresh`,
      {},
      { headers: { 'X-Refresh-Token': refreshToken } },
    );
    const refreshedAuth = get(response, 'data.auth', get(response, 'data', {}));
    const newAccessToken = get(refreshedAuth, 'accessToken', '');
    if (!newAccessToken) throw new Error('Refresh response did not include an access token');

    const nextAuthData = { ...authData, ...refreshedAuth };
    setItem(storageTypes.localStorage, 'AuthData', nextAuthData);
    store.dispatch({ type: DISPATCH_AUTH_TOKEN, authData: nextAuthData });
    notifyAuthTokenUpdated(nextAuthData);
    if (isNativeWebView && window.ReactNativeWebView) {
      const state = store.getState();
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          eventType: {
            type: 'loginData',
            data: {
              guest: get(state, 'authentication.loginData', {}),
              auth: nextAuthData,
              rewardGuestDetails: get(state, 'rewards.rewardGuestDetails', {}),
            },
          },
        }),
      );
    }
    return nextAuthData;
  };

  const clearExpiredSession = () => {
    if (sessionExpired) return;
    sessionExpired = true;
    setItem(storageTypes.localStorage, 'AuthData', {});
    setItem(storageTypes.localStorage, 'GuestLoginData', {});
    store.dispatch({ type: DISPATCH_AUTH_TOKEN, authData: {} });
    store.dispatch({ type: ATTEMPT_LOGIN_SUCCESS, loginData: {} });
    store.dispatch(
      apiErrorResponseAction({
        code: serverErrorCodes.invalidToken,
        message: 'Session expired',
        statusCode: 401,
      }),
    );
    window.dispatchEvent(new CustomEvent('qwiky:session-expired'));
    if (typeof window.stopExpertBookingPolling === 'function') {
      window.stopExpertBookingPolling();
    }
  };

  const handleLogoutError = (error) => {
    let errorData = {};
    try {
      const statusCode = get(error, 'response.status', '');
      const responseData = get(error, 'response.data', error);
      errorData = responseData;
      errorData['statusCode'] = statusCode ? statusCode : get(error, 'response.statusCode', '');
      if ([401, 403].includes(statusCode)) {
        errorData.code = serverErrorCodes.invalidToken;
        errorData.message = 'Session expired';
      }
    } catch (error) {
      errorData = get(error, 'response.data', error);
    }
    if (!errorData) {
      console.error('No error data found in error object:', error);
      return;
    }

    // Dispatching error only after bootstrapped (redux store is initialized)
    try {
      if (persistor.getState().bootstrapped) {
        store.dispatch(apiErrorResponseAction(errorData));
      } else {
        const unsubscribe = persistor.subscribe(() => {
          if (persistor.getState().bootstrapped) {
            unsubscribe(); // Stop listening after bootstrapped
            store.dispatch(apiErrorResponseAction(errorData));
          }
        });
      }
    } catch (error) {
      store.dispatch(apiErrorResponseAction(errorData));
    }
  };

  const handlePendingRequests = ({ newToken, error }) => {
    const queuedRequests = pendingRequests;
    pendingRequests = [];

    if (newToken) {
      for (const request of queuedRequests) {
        try {
          if (typeof request.resolve === 'function' && request.config) {
            request.config.headers = request.config.headers || {};
            request.config.headers['Authorization'] = `Bearer ${newToken}`;
            if (request.phase === 'response') {
              request.config._retry = true;
            }
            request.resolve(
              request.phase === 'response' ? objAxios(request.config) : request.config,
            );
          } else {
            console.warn('Skipped malformed pending request (resolve):', request);
          }
        } catch (err) {
          console.error('Failed to resolve pending request:', err, request);
        }
      }
    } else {
      for (const request of queuedRequests) {
        try {
          if (typeof request.reject === 'function') {
            request.reject(error);
          } else {
            console.warn('Skipped malformed pending request (reject):', request);
          }
        } catch (err) {
          console.error('Failed to reject pending request:', err, request);
        }
      }
    }
  };

  const isTerminalRefreshError = (error) => {
    const status = get(error, 'response.status');
    const code = get(error, 'response.data.code', '');
    return (
      [400, 401, 403].includes(status) ||
      ['REFRESH_TOKEN_EXPIRED', serverErrorCodes.invalidToken].includes(code) ||
      ['Refresh token expired', 'Refresh response did not include an access token'].includes(
        get(error, 'message', ''),
      )
    );
  };

  const handleAuthenticationError = async (error, { retryOriginal = true } = {}) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      console.error('[Auth][TokenRefresh] Original request is unavailable');
      return Promise.reject(error);
    }
    if (originalRequest._retry) return Promise.reject(error);
    originalRequest._retry = true;
    setIsApiRequestOnHold(true);
    console.info('[Auth][TokenRefresh] Refresh started', {
      requestUrl: get(originalRequest, 'url', ''),
      queuedRequestCount: pendingRequests.length,
    });
    try {
      const authData = getItem(storageTypes.localStorage, 'AuthData');
      // All Axios instances share this promise. This prevents a burst of 403s
      // (for example from booking polling) from creating parallel refresh calls.
      if (!refreshPromise) refreshPromise = handleTokenRefresh(authData);
      const refreshRes = await refreshPromise;
      const newToken = get(refreshRes, 'accessToken', '');

      if (newToken) {
        // Update the failed request with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        // Release the request interceptor before retrying. Otherwise, retried requests
        // are queued again and the refresh flow waits on itself indefinitely.
        refreshPromise = null;
        lastRefreshAt = Date.now();
        sessionExpired = false;
        setIsApiRequestOnHold(false);

        // Process all pending requests with new token after releasing the API hold.
        handlePendingRequests({ newToken });
        console.info('[Auth][TokenRefresh] Pending requests released', {
          requestUrl: get(originalRequest, 'url', ''),
          retryOriginal,
        });

        // A proactive request-interceptor refresh only needs the new token. A response
        // interceptor refresh retries the request which originally received the 401.
        return retryOriginal ? await objAxios(originalRequest) : newToken;
      } else {
        // Token refresh failed, reject all pending requests
        console.log('Token refresh failed');
        handlePendingRequests({ error });
        clearExpiredSession();
        handleLogoutError(error);
        return Promise.reject(error);
      }
    } catch (refreshError) {
      handlePendingRequests({ error: refreshError });
      if (isTerminalRefreshError(refreshError)) {
        clearExpiredSession();
        handleLogoutError(refreshError);
      }
      return Promise.reject(refreshError);
    } finally {
      refreshPromise = null;
      setIsApiRequestOnHold(false);
    }
  };

  registerWorkerTokenRefreshHandler(({ accessToken: failedAccessToken = '' } = {}) => {
    const latestAuthData = getItem(storageTypes.localStorage, 'AuthData');
    const latestAccessToken = get(latestAuthData, 'accessToken', '');

    // A polling response can arrive after another request has already refreshed the
    // token. Re-broadcast the current credential instead of rotating it a second time.
    if (failedAccessToken && latestAccessToken && failedAccessToken !== latestAccessToken) {
      notifyAuthTokenUpdated(latestAuthData);
      return Promise.resolve(latestAccessToken);
    }

    return handleAuthenticationError(
      { config: { url: 'notification-worker-polling', headers: {} } },
      { retryOriginal: false },
    );
  });

  objAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const isOtpApi = get(originalRequest, 'url', '').includes('/auth/otp');

      if (isAuth401Error(error) && !isOtpApi) {
        const latestAuthData = getItem(storageTypes.localStorage, 'AuthData');
        const latestAccessToken = get(latestAuthData, 'accessToken', '');
        const failedAuthorizationHeader = get(originalRequest, 'headers.Authorization', '');
        const failedAccessToken = failedAuthorizationHeader.replace(/^Bearer\s+/i, '');

        // A request can receive its 401 just after another request has completed the
        // shared refresh. Retry it with the already-refreshed token instead of
        // starting another refresh cycle.
        if (
          latestAccessToken &&
          latestAccessToken !== failedAccessToken &&
          !get(originalRequest, '_retry', false)
        ) {
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${latestAccessToken}`;
          console.info('[Auth][TokenRefresh] Retrying late 401 with current token', {
            requestUrl: get(originalRequest, 'url', ''),
          });
          return objAxios(originalRequest);
        }

        // Requests that were sent with the just-invalidated token can arrive
        // immediately after refresh. Reuse the fresh token once instead of
        // starting another refresh cycle.
        if (latestAccessToken && Date.now() - lastRefreshAt < 2000 && !get(originalRequest, '_retry', false)) {
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${latestAccessToken}`;
          return objAxios(originalRequest);
        }

        if (getIsApiRequestOnHold()) {
          // Queue the request while token refresh is in progress
          return new Promise((resolve, reject) => {
            pendingRequests.push({
              resolve,
              reject,
              config: originalRequest,
              phase: 'response',
            });
          });
        }
        return handleAuthenticationError(error); // ✅ return important
      }
      return Promise.reject(error);
    },
  );

  return objAxios;
};

const client = (params = {}) => buildAPIInstance(params);

export default client;
